import type { InfiniteData } from "@tanstack/react-query";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";

import type { PortfolioAdminTicketDtoPaginationResultDto, PortfolioMinimalTicketDto } from "@/api/types";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { useModalState } from "@/components/Modal/Modal";
import { PortfolioTicketOperationalAnalyticsSummary } from "@/components/OperationalAnalytics/OperationalAnalyticsSummaries";
import { filterProjectsByFilters } from "@/components/ProjectFilter/filterProjectsByFilters";
import { addPortfolioProjectFilterQueryParams } from "@/components/ProjectFilter/portfolioProjectFilterQueryParams";
import { parseQueryParamToList } from "@/helpers/query-params";
import { useLinkedProjects } from "@/hooks/Network/useLinkedProjects";
import { useDebounceValue } from "@/hooks/useDebounce";
import {
  getLocalStorageValue,
  getSessionStorageValue,
  updateSessionStorage,
  useUpdateLocalStorage,
  useUpdateSessionStorage,
} from "@/hooks/useLocalStorage";
import { useQueryParams } from "@/hooks/useQueryParam";
import { mapTicketAssigneeIdsToApi } from "@/modules/tickets/pages/list/components/TicketAssigneeFilter";
import { getActiveTicketFilterCount } from "@/modules/tickets/pages/list/helpers";
import { portfolioTicketsMutations } from "@/queries/portfolio-tickets/mutations";
import { usePortfolioTicketsQueries } from "@/queries/portfolio-tickets/queryOptions";
import type { AdminTicketTabs, TicketSorting } from "@/queries/tickets/constants";
import {
  CLOSED_TICKET_TAB,
  IN_PROGRESS_TICKET_TAB,
  NEW_AND_INPROGRESS_TICKET_TAB,
  NEW_TICKET_TAB,
  REMINDER_TICKET_TAB,
  ticketRatingValues,
  ticketSortingValues,
  ticketVisibilityValues,
} from "@/queries/tickets/constants";
import { ticketStorageKeys } from "@/queries/tickets/filters";
import { ticketMutations } from "@/queries/tickets/mutations";
import { QUERY_KEYS } from "@/query-keys";
import { routes } from "@/routes";
import type { ApiQueryParams } from "@/types/api-types";

import type { PortfolioRouteParams } from "../../routes";
import type { PortfolioTicketsFilterValues } from "./components/PortfolioTicketsFilterDrawer";
import { PortfolioTicketListContent, type PortfolioTicketListContentProps } from "./PortfolioTicketListContent";
import {
  canAccessPortfolioTicketAnalyticsDetails,
  canAccessPortfolioTicketOperationalAnalyticsSummary,
  resolvePortfolioTicketProjectScope,
} from "./portfolioTicketProjectScope";

const TABS = [
  NEW_TICKET_TAB,
  IN_PROGRESS_TICKET_TAB,
  CLOSED_TICKET_TAB,
  REMINDER_TICKET_TAB,
  NEW_AND_INPROGRESS_TICKET_TAB,
] as const satisfies AdminTicketTabs[];

type TicketsSortingParams = { [key in AdminTicketTabs]: TicketSorting };
type AllTicketsQueryParams = ApiQueryParams<"getAdminPortfolioTicketsV1", 0>;
export const DEFAULT_TICKETS_TAB = TABS[0];
const DEFAULT_TAB_SORTING: TicketsSortingParams = {
  new: "oldest",
  inProgress: "mostRecentActivity",
  closed: "mostRecentActivity",
  reminder: "mostRecentReminder",
  newAndInProgress: "mostRecentActivity",
};
const TICKET_TAB_SORT_KEY = ticketStorageKeys.tabSort();
const TICKET_TAB_RESIDENT_FOCUS_KEY = ticketStorageKeys.residentFocusMode();
const PORTFOLIO_TICKET_FILTER_STORAGE_KEY = ticketStorageKeys.portfolio_filters();
const TICKET_PAGE_FILTER_PARAM_KEYS = [
  "tab",
  "search",
  "sort",
  "statusKeys",
  "categoryNames",
  "assigneeAccountIds",
  "assigneeMode",
  "ratings",
  "visibility",
  "isUnreadOnly",
] as const satisfies readonly (keyof PortfolioRouteParams["tickets"])[];

function getTicketPageFilterParams(
  queryParams: Partial<PortfolioRouteParams["tickets"]>,
): Partial<PortfolioRouteParams["tickets"]> {
  return Object.fromEntries(TICKET_PAGE_FILTER_PARAM_KEYS.map((key) => [key, queryParams[key]]));
}

export function PortfolioTicketListPage(): React.ReactNode {
  const location = useLocation();
  const [queryParams, setQueryParams] = useQueryParams<PortfolioRouteParams["tickets"]>();
  const [sorting, setSorting] = useState<TicketsSortingParams>(() => {
    const storage = getLocalStorageValue<Partial<TicketsSortingParams>>(TICKET_TAB_SORT_KEY, {});
    const urlSort =
      queryParams.sort && ticketSortingValues.includes(queryParams.sort as Exclude<TicketSorting, undefined>)
        ? (queryParams.sort as TicketSorting)
        : undefined;
    const activeTabKey = (
      queryParams.tab && TABS.includes(queryParams.tab as any) ? queryParams.tab : DEFAULT_TICKETS_TAB
    ) as AdminTicketTabs;

    const defaults: TicketsSortingParams = {
      new: storage.new && ticketSortingValues.includes(storage.new) ? storage.new : "oldest",
      inProgress:
        storage.inProgress && ticketSortingValues.includes(storage.inProgress)
          ? storage.inProgress
          : "mostRecentActivity",
      closed: storage.closed && ticketSortingValues.includes(storage.closed) ? storage.closed : "mostRecentActivity",
      reminder: "mostRecentReminder",
      newAndInProgress:
        storage.newAndInProgress && ticketSortingValues.includes(storage.newAndInProgress)
          ? storage.newAndInProgress
          : "mostRecentActivity",
    };

    return urlSort ? { ...defaults, [activeTabKey]: urlSort } : defaults;
  });
  const [residentFocusMode, setResidentFocusMode] = useState<boolean>(() =>
    getSessionStorageValue<boolean>(TICKET_TAB_RESIDENT_FOCUS_KEY, false),
  );

  // Restore page-owned ticket filters from session storage if the URL has none.
  useEffect(() => {
    const hasPageFilterParams = TICKET_PAGE_FILTER_PARAM_KEYS.some((key) => queryParams[key] != null);
    if (!hasPageFilterParams) {
      const stored = getSessionStorageValue<Partial<PortfolioRouteParams["tickets"]>>(
        PORTFOLIO_TICKET_FILTER_STORAGE_KEY,
        {},
      );
      const storedPageFilters = getTicketPageFilterParams(stored);
      if (Object.values(storedPageFilters).some(Boolean)) {
        setQueryParams(storedPageFilters, { replace: true });
      }
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const linkedProjects = useLinkedProjects().data;
  const queryClient = useQueryClient();
  const openTicketModal = useModalState<PortfolioMinimalTicketDto>();
  const markTicketAsRead = portfolioTicketsMutations.usePortfolioTicketsMarkAsRead();

  const [search, setSearch] = useState(() => queryParams.search ?? "");
  const activeTab = (
    queryParams.tab && TABS.includes(queryParams.tab as any) ? queryParams.tab : DEFAULT_TICKETS_TAB
  ) as AdminTicketTabs;
  const currFilter = useMemo(() => {
    return {
      owner: parseQueryParamToList(queryParams.owner),
      maintenance: parseQueryParamToList(queryParams.maintenance),
      projectIds: parseQueryParamToList(queryParams.projectIds),
      statusKeys: parseQueryParamToList(queryParams.statusKeys),
      categoryNames: parseQueryParamToList(queryParams.categoryNames),
      assigneeAccountIds: parseQueryParamToList(queryParams.assigneeAccountIds),
      assigneeMode: queryParams.assigneeMode === "exclude" ? "exclude" : "include",
      ratings: parseQueryParamToList(queryParams.ratings)
        ?.map(Number)
        .filter((n) => ticketRatingValues.includes(n as (typeof ticketRatingValues)[number])),
      visibility:
        queryParams.visibility && ticketVisibilityValues.includes(queryParams.visibility as any)
          ? (queryParams.visibility as PortfolioTicketsFilterValues["visibility"])
          : undefined,
      isUnreadOnly: queryParams.isUnreadOnly === "true" ? true : undefined,
    } satisfies PortfolioTicketsFilterValues;
  }, [
    queryParams.owner,
    queryParams.maintenance,
    queryParams.projectIds,
    queryParams.statusKeys,
    queryParams.categoryNames,
    queryParams.assigneeAccountIds,
    queryParams.assigneeMode,
    queryParams.ratings,
    queryParams.visibility,
    queryParams.isUnreadOnly,
  ]);

  useUpdateSessionStorage(TICKET_TAB_RESIDENT_FOCUS_KEY, residentFocusMode);
  useUpdateLocalStorage(TICKET_TAB_SORT_KEY, sorting);

  const debouncedQuery = useDebounceValue(search.toLowerCase().trim());

  useEffect(() => {
    setQueryParams({ search: debouncedQuery || undefined }, { replace: true });
  }, [debouncedQuery, setQueryParams]);

  const sortBy = (residentFocusMode ? "leastRecentActivity" : sorting[activeTab] || "newest") satisfies TicketSorting;

  const handleUpdateSorting = useCallback(
    (value: TicketSorting) => {
      setSorting((oldSorting) => ({
        ...oldSorting,
        [activeTab]: value,
      }));
      setQueryParams({ sort: value }, { replace: true });
    },
    [activeTab, setQueryParams],
  );

  const handleChangeTab = useCallback(
    (tab: AdminTicketTabs) => {
      const defaultSort = DEFAULT_TAB_SORTING[tab];
      const newQueryParams = {
        ...queryParams,
        tab,
        sort: defaultSort,
      };

      setSorting((prev) => ({ ...prev, [tab]: defaultSort }));
      setQueryParams(newQueryParams);
      updateSessionStorage(PORTFOLIO_TICKET_FILTER_STORAGE_KEY, getTicketPageFilterParams(newQueryParams));
    },
    [queryParams, setQueryParams],
  );

  const handleUpdateFilter = (newFilter: PortfolioTicketsFilterValues) => {
    const newQueryParams: PortfolioRouteParams["tickets"] = {
      ...queryParams,
      statusKeys: newFilter.statusKeys?.length ? newFilter.statusKeys.join(",") : undefined,
      categoryNames: newFilter.categoryNames?.length ? newFilter.categoryNames.join(",") : undefined,
      assigneeAccountIds: newFilter.assigneeAccountIds?.length ? newFilter.assigneeAccountIds.join(",") : undefined,
      assigneeMode:
        newFilter.assigneeAccountIds?.length && newFilter.assigneeMode === "exclude" ? "exclude" : undefined,
      owner: newFilter.owner?.length ? newFilter.owner.join(",") : undefined,
      maintenance: newFilter.maintenance?.length ? newFilter.maintenance.join(",") : undefined,
      projectIds: newFilter.projectIds?.length ? newFilter.projectIds.join(",") : undefined,
      ratings: newFilter.ratings?.length ? newFilter.ratings.join(",") : undefined,
      visibility: newFilter.visibility,
      isUnreadOnly: newFilter.isUnreadOnly ? "true" : undefined,
    };

    setQueryParams(newQueryParams);
    updateSessionStorage(PORTFOLIO_TICKET_FILTER_STORAGE_KEY, getTicketPageFilterParams(newQueryParams));
  };

  const projectsInScope = useMemo(() => {
    return filterProjectsByFilters(linkedProjects || [], {
      owner: currFilter.owner,
      maintenance: currFilter.maintenance,
    }).filter((x) => !currFilter.projectIds?.length || currFilter.projectIds.includes(x.id));
  }, [linkedProjects, currFilter.owner, currFilter.maintenance, currFilter.projectIds]);

  const projectIdsToFilter = useMemo(() => {
    const filteredProjects = projectsInScope.map((project) => project.id);

    if (linkedProjects && linkedProjects.length === filteredProjects.length) {
      return undefined;
    }

    // There's limits to the number of projects we can filter. URLs can only be a certain length.
    // We should stay under 2000 characters.
    // 1 projectId is ~15 characters
    // 100 projectIds is ~1500 characters
    // So if its more than a 100 projects we just display all your projects.
    if (filteredProjects.length > 100) {
      return undefined;
    }

    return filteredProjects;
  }, [linkedProjects, projectsInScope]);

  const effectiveProjectScope = resolvePortfolioTicketProjectScope({
    linkedProjects: linkedProjects ?? [],
    projectsInScope,
    projectIdsToFilter,
  });

  const query = useMemo(
    () =>
      ({
        Ratings: currFilter.ratings,
        UnreadOnly: currFilter.isUnreadOnly,
        Tab: activeTab,
        StatusKeys: currFilter.statusKeys,
        CategoryNames: currFilter.categoryNames,
        AssigneeAccountIds: mapTicketAssigneeIdsToApi(currFilter.assigneeAccountIds),
        AssigneeMode: currFilter.assigneeMode,
        Filter: currFilter.visibility,
        ProjectIds: projectIdsToFilter,
        SortBy: sortBy,
        Search: debouncedQuery,
        ResidentFocusMode: residentFocusMode,
      }) satisfies AllTicketsQueryParams,
    [
      currFilter.ratings,
      currFilter.isUnreadOnly,
      currFilter.statusKeys,
      currFilter.categoryNames,
      currFilter.assigneeAccountIds,
      currFilter.assigneeMode,
      currFilter.visibility,
      activeTab,
      projectIdsToFilter,
      sortBy,
      debouncedQuery,
      residentFocusMode,
    ],
  );
  const portfolioTicketsQueries = usePortfolioTicketsQueries();
  const {
    data: ticketData,
    hasNextPage: hasMoreTickets,
    isFetching: isFetchingTickets,
    isFetchingNextPage: isLoadingMoreTickets,
    fetchNextPage: loadMoreTickets,
    error: ticketsError,
  } = useInfiniteQuery(portfolioTicketsQueries.list(query));
  const { data: tabsStats, error: tabsStatsError } = useQuery(portfolioTicketsQueries.tabsStats(query));
  const isFilterActive = getActiveTicketFilterCount(currFilter) > 0 || !!debouncedQuery || residentFocusMode;

  const { data: totalTicketsInTab, error: totalTicketsInTabError } = useQuery({
    ...portfolioTicketsQueries.totalTicketsInTab(activeTab),
    enabled: isFilterActive,
  });
  const { data: allTicketIds } = useQuery({
    ...portfolioTicketsQueries.ticketIds(query),
    enabled: activeTab === "new",
  });
  const deleteTicketMutation = ticketMutations.useDeleteTicket();

  const totalTickets = ticketData?.pages[0].total;
  const tickets = useMemo(() => ticketData?.pages.flatMap((x) => x.items) ?? [], [ticketData]);

  const [session, setSession] = useState(() => {
    const result = getSessionStorageValue<
      | {
          items: PortfolioMinimalTicketDto[];
          missed: PortfolioMinimalTicketDto[];
          created: number;
        }
      | undefined
    >("ticket-session", undefined);

    if (result) {
      // Clear session if it's older than 24 hours
      const oneDay = 1000 * 60 * 60 * 24;
      if (result.created + oneDay < Date.now()) {
        return undefined;
      }
    }

    return result;
  });
  const sessionFinishedModal = useModalState<{ missedItems: number }>();
  useUpdateSessionStorage("ticket-session", session);

  if (openTicketModal.isOpen) {
    if (!session && openTicketModal.data && allTicketIds) {
      const openTicketIndex = allTicketIds.findIndex((x) => x.ticketId === openTicketModal.data?.ticketId);

      setSession({
        items: allTicketIds.slice(openTicketIndex),
        missed: allTicketIds.slice(0, openTicketIndex),
        created: new Date().getTime(),
      });
    }
  } else if (session) {
    setSession(undefined);
  }

  function goToNextTicket() {
    if (!session) {
      return;
    }

    if (!openTicketModal.data) {
      return;
    }

    const currentTicketIndex = session.items.findIndex((x) => x.ticketId === openTicketModal.data?.ticketId);
    const nextTicket = session.items[currentTicketIndex + 1];
    if (nextTicket) {
      openTicketModal.open(nextTicket);
    } else {
      openTicketModal.requestClose();
      setSession(undefined);
      sessionFinishedModal.open({ missedItems: session.missed.length });
    }
  }

  // Mark ticket as read when it's opened without network request
  useEffect(() => {
    if (openTicketModal.data?.ticketId) {
      queryClient.setQueryData<InfiniteData<PortfolioAdminTicketDtoPaginationResultDto>>(
        QUERY_KEYS.PORTFOLIO_TICKETS_LIST(query),
        (oldInfiniteData) =>
          oldInfiniteData === undefined
            ? undefined!
            : {
                ...oldInfiniteData,
                pages: [
                  ...oldInfiniteData.pages.map((page) => ({
                    ...page,
                    items: page.items.map((ticket) =>
                      ticket.id === openTicketModal.data?.ticketId ? { ...ticket, hasUnreadActivity: false } : ticket,
                    ),
                  })),
                ],
              },
      );
    }
  }, [openTicketModal?.data?.ticketId, query, queryClient]);

  if (ticketsError || totalTicketsInTabError || tabsStatsError) {
    return <ErrorPage error={ticketsError} />;
  }

  const ticketAnalyticsPath = effectiveProjectScope.preserveProjectFiltersInDetailsLink
    ? addPortfolioProjectFilterQueryParams(routes.portfolio.analyticsTickets(), location.search)
    : routes.portfolio.analyticsTickets();
  const ticketAnalyticsDetailsHref = `${ticketAnalyticsPath}${ticketAnalyticsPath.includes("?") ? "&" : "?"}dateRange=last6Months`;
  const canAccessTicketAnalyticsDetails = canAccessPortfolioTicketAnalyticsDetails(effectiveProjectScope.projects);
  const canAccessTicketAnalyticsSummary = canAccessPortfolioTicketOperationalAnalyticsSummary(
    effectiveProjectScope.projects,
  );

  const contentProps: PortfolioTicketListContentProps = {
    tab: activeTab,
    onChangeTab: handleChangeTab,
    search,
    onChangeSearch: setSearch,
    residentFocusMode,
    onUpdateResidentFocusMode: setResidentFocusMode,
    sorting: sortBy,
    onUpdateSorting: handleUpdateSorting,
    filter: currFilter,
    onUpdateFilter: handleUpdateFilter,
    totalTickets,
    totalTicketsInTab: isFilterActive ? totalTicketsInTab?.tickets : totalTickets,
    tabsStats,
    tickets,
    isLoadingTickets: isFetchingTickets,
    hasMoreTickets,
    loadMoreTickets,
    isLoadingMoreTickets,
    deleteTicket: deleteTicketMutation.mutateAsync,
    markAsRead: () => markTicketAsRead.mutate(undefined),
    showNextTicketBtn: !!session && activeTab === "new",
    goToNextTicket,
    openTicketModal,
    sessionFinishedModal,
    leftInSession: session
      ? session.items.length - session.items.findIndex((x) => x.ticketId === openTicketModal.data?.ticketId)
      : undefined,
    analyticsSummary: canAccessTicketAnalyticsSummary ? (
      <PortfolioTicketOperationalAnalyticsSummary
        projectIds={effectiveProjectScope.projects.map((project) => project.id)}
        detailsHref={canAccessTicketAnalyticsDetails ? ticketAnalyticsDetailsHref : undefined}
      />
    ) : undefined,
  };

  return <PortfolioTicketListContent {...contentProps} />;
}
